import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createAppTheme } from "@/theme/theme";

type Mode = "light" | "dark";
type Preference = "light" | "dark" | "system";

interface ThemeModeContextType {
  mode: Mode;
  preference: Preference;
  toggleMode: () => void;
  setMode: (pref: Preference) => void;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(
  undefined,
);

function getSystemMode(): Mode {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getInitialPreference(): Preference {
  const stored = localStorage.getItem("pdfglyph-theme");
  if (stored === "light" || stored === "dark" || stored === "system")
    return stored;
  return "system";
}

function resolveMode(pref: Preference): Mode {
  return pref === "system" ? getSystemMode() : pref;
}

export function ThemeContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [preference, setPreferenceState] =
    useState<Preference>(getInitialPreference);
  const [mode, setModeState] = useState<Mode>(() =>
    resolveMode(getInitialPreference()),
  );

  const setMode = useCallback((pref: Preference) => {
    setPreferenceState(pref);
    setModeState(resolveMode(pref));
    localStorage.setItem("pdfglyph-theme", pref);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === "light" ? "dark" : "light");
  }, [mode, setMode]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      if (preference === "system") {
        setModeState(e.matches ? "dark" : "light");
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({ mode, preference, toggleMode, setMode }),
    [mode, preference, toggleMode, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextType {
  const context = useContext(ThemeModeContext);
  if (!context)
    throw new Error("useThemeMode must be used within ThemeContextProvider");
  return context;
}
